import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close as CloseIcon, Email as EmailIcon } from '@material-ui/icons';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(1),
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  reportTypeChip: {
    marginLeft: theme.spacing(1),
  },
  list: {
    padding: 0,
  },
  listItem: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  emailText: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
  },
}));

interface RecipientsDialogProps {
  open: boolean;
  onClose: () => void;
  orgName: string;
  reportType: string;
  recipients: string[];
  loading: boolean;
}

export const RecipientsDialog: React.FC<RecipientsDialogProps> = ({
  open,
  onClose,
  orgName,
  reportType,
  recipients,
  loading,
}) => {
  const classes = useStyles();

  const getReportTypeLabel = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'weekly':
        return 'Weekly';
      case 'monthly':
        return 'Monthly';
      case 'daily_csv':
        return 'Daily CSV';
      default:
        return type;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <EmailIcon color="action" />
          <Typography variant="h6">Recipients</Typography>
          <Chip label={getReportTypeLabel(reportType)} size="small" className={classes.reportTypeChip} />
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          {orgName}
        </Typography>

        {loading ? (
          <Box className={classes.loadingState}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">
              Loading recipients...
            </Typography>
          </Box>
        ) : recipients.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">No recipients configured</Typography>
          </Box>
        ) : (
          <List className={classes.list} dense>
            {recipients.map((email, index) => (
              <ListItem key={index} className={classes.listItem}>
                <ListItemText primary={email} primaryTypographyProps={{ className: classes.emailText }} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};
